import { useCallback, useMemo, useState } from "react";
import { SwitchField } from "src/components/FormikComponents/SwitchField";
import { Grid, Typography, useTheme, Card, CardContent, CardActions, Button, Theme, alpha } from "@mui/material";
import { INewGroundEvent } from "@vmosolutions/oc-interfaces";
import { EventTypes } from "../FlightBuilder";
import classNames from "classnames";
import Addbox from "@mui/icons-material/AddBox";
import Remove from "@mui/icons-material/Remove";
import EditRounded from "@mui/icons-material/EditRounded";
import { GroundEventRowEdit } from "./GroundEventRowEdit";
import { GroundEventRowDisplay } from "./GroundEventRowDisplay";

interface IGroundEventRowsProps {
  mosaicId: string;
  legs: INewGroundEvent[];
  legsAreDirty: boolean;
  resetAllChangesToLegs: () => void;
  onSubmit: (groundEventValues: INewGroundEvent) => void;
  addLeg: () => void;
  removeLeg: (id: string) => void;
  eventType?: EventTypes;
  selectedRowIndex?: number;
  displayAsUTC: boolean;
  setDisplayAsUTC: (state: boolean) => void;
}

const formActionStyles = (theme: Theme): any => ({
  marginTop: "10px",
  "& .form_button": {
    width: "auto",
    height: 30,
    fontSize: 11,
    lineHeight: "12px",
    padding: "2px 8px",
    margin: "0 2px",
  },
});

const gridContainerStyles = (theme: Theme): any => ({
  width: "100%",
  height: "100%",
  display: "flex",
  alignContent: "flex-start",
  position: "relative",
  padding: "10px",
  flexGrow: 1,
  "& .edit_icon": {
    width: 16,
    height: 16,
    border: `1px solid ${theme.palette.primary.contrastText}`,
    color: theme.palette.primary.contrastText,
    borderRadius: "3px",
    "&.active": {
      border: `1px solid ${theme.custom.forms.dirtyColor}`,
      color: theme.custom.forms.dirtyColor,
    },
  },
  "& .dialog_spinner_progress": { margin: -10 },
  "& .field-group": {
    flexDirection: "row",
    width: 380,
  },
  "& .formik_field": {
    width: 363,
    "&.note, &.comment": { width: 736 },
  },
  "& .error-message": { fontSize: 10, margin: 0 },
  "& p.required_fields_label, & p.optional_fields_label, & .crewslot_fields_label": {
    margin: 0,
    marginLeft: "74px",
    fontWeight: 300,
    fontSize: 10,
    textTransform: "uppercase",
    color: theme.custom.forms.labels,
  },
  "& p.optional_fields_label": {
    marginLeft: "672px",
  },
  "& .checkbox": { width: 36 },
  "& .flight_row": {
    width: "auto",
    borderTop: `1px solid ${theme.palette.background.default}`,
  },
  "& .required_fields": {
    minWidth: 750,
  },
  "& .required_fields, & .crewslot_fields": {
    backgroundColor: alpha(theme.custom.forms.labels, 0.2),
    margin: "2px 0px",
    width: "auto",
    "&.invalid": {
      backgroundColor: alpha(theme.custom.forms.errorColor, 0.2),
    },
  },
  "& .optional_fields": {
    margin: "2px 0px",
    width: "auto",
  },
  "& .flight_leg": {
    textAlign: "right",
    width: 22,
    lineHeight: "24px",
    fontSize: 14,
    margin: "5px",
    padding: "0px 5px",
  },
  "& .flight_number": {
    width: 100,
    margin: "5px",
    "& label": { display: "none" },
    "& div.input_control": {
      borderRadius: "3px",
      "& div.input-base": {
        borderRadius: "3px",
      },
    },
    "&.type_assigned": {
      "& div.input-base": {
        backgroundColor: "inherit",
        color: "inherit !important",
        "& input": { color: "inherit !important" },
      },
    },
  },
  "& .mock_event_chip": {
    margin: "5px",
    height: 24,
    width: 100,
    padding: "2px 8px",
    borderRadius: "3px",
    fontWeight: 600,
    textAlign: "center",
  },
  "& .event_type": {
    "&.type_assigned": {
      "& div.input-base": {
        backgroundColor: "inherit",
        color: "inherit !important",
        "& input": { color: "inherit !important" },
      },
    },
  },
  "& .location, .origin, .destination, .flight_type, .event_type, .aircraft_config": {
    width: 100,
    "& .input_control": {
      width: "100%",
      "& .react-select-container": {
        width: "100%",
        "& .react-select__control ": { height: 24 },
      },
    },
  },
  "& .MuiFormControl-root.aircraft": {
    marginLeft: "10px",
    width: 100,
    "& .input_control": {
      width: "100%",
      "& .react-select-container": {
        width: "100%",
        "& .react-select__control ": { height: 24 },
      },
    },
  },
  "& .flags, & .customer": {
    width: 344,
    marginLeft: "23px",
    marginTop: "10px",
    "& .input_control": {
      width: "100%",
      "& .react-select-container": {
        width: "100%",
        "& .react-select__control ": { height: 24 },
      },
    },
  },
  "& .schedule_out, & .schedule_in": {
    width: 162,
  },
  "& .recurrent_schedule_out, & .recurrent_schedule_in": { width: 62 },
  "& .block_duration": {
    width: 55,
    opacity: 0.5,
    margin: "5px 10px 5px 5px",
    padding: "2px 8px",
  },
  "& .day_offset": {
    width: "auto",
    "& label": { display: "none" },
  },
  "& .oc_datetime": {
    // used in flight
    alignItems: "flex-start",
  },
  "& .scheduled_start, & .scheduled_end": {
    width: 163,
  },
  "& .scheduled_out, & .scheduled_in": {
    // used in recurring flight
    width: "auto",
    alignItems: "flex-start",
    "& .react-datepicker-wrapper": { display: "none" },
    "& .input_control": {
      "& label": { width: 80 },
      "& .time-input": { width: 70, margin: 0 },
    },
  },
  "& .position_count": {
    width: "auto",
    "& label": { width: "auto" },
    "& .input_control": {
      "& div.input-base": {
        maxWidth: 50,
      },
    },
  },
  // Flight status
  "& .status_message": {
    display: "flex",
    alignItems: "center",
    padding: "2px 5px",
    maxWidth: 300,
    fontSize: 12,
    lineHeight: "12px",
    fontWeight: 300,
    color: theme.palette.primary.contrastText,
    "& .flightchip, & .eventchip": {
      margin: "0px 6px ",
      "&:hover": { width: 120 },
    },
    "& .status_arrow": {
      color: alpha(theme.palette.background.paper, 0.5),
    },
    "& .conflict_icon": {
      margin: "0px 5px",
    },
    "& .status_icon": {
      display: "flex",
      "& button": {
        backgroundColor: alpha(theme.palette.background.paper, 0.5),
        borderRadius: "3px",
        padding: 0,
        margin: "0px 5px",
        width: 24,
        height: 24,
        overflow: "hidden",
      },

      "& .icon_foreground": {
        width: "100%",
        marginTop: "1px",
        // Active State - blue
        "&.pending": {
          marginTop: "3px",
          "& path": {
            fill: `${theme.palette.primary.main} !important`,
          },
        },
        // Conflict State - orange
        "&.conflict > path": {
          fill: `${theme.palette.secondary.main} !important`,
        },
        // Error State - red
        "&.failed > path": {
          fill: `${theme.palette.error.main} !important`,
        },
        // Success State - green
        "&.success": {
          marginTop: "3px",
          "& path, & polygon": {
            fill: `${theme.palette.success.main} !important`,
          },
        },
      },
    },
  },
});

/**
 * Base Ground Event Form
 * @param props -
 */
export const GroundEventRows = (props: IGroundEventRowsProps): JSX.Element => {
  const {
    mosaicId,
    legs,
    legsAreDirty,
    resetAllChangesToLegs,
    onSubmit,
    addLeg,
    removeLeg,
    eventType,
    displayAsUTC,
    setDisplayAsUTC,
  } = props;

  const mosaicPane = useMemo(() => document.getElementById(`${mosaicId}-panel`), [mosaicId]);
  const theme = useTheme();
  const [editRow, setEditRow] = useState<number>(0);
  const [focusField, setFocusField] = useState<string>(null);

  const handleAddNewLeg = useCallback(
    (lastRow: boolean): void => {
      if (lastRow) addLeg();
      setEditRow((prev) => prev + 1);
      setFocusField("eventType");
    },
    [addLeg]
  );

  return (
    <Card classes={{ root: classNames("flights_form_card") }}>
      <CardContent className="card_content" style={{ padding: 10, minWidth: 1150 }}>
        <Grid container direction="column">
          <Grid container sx={{ ...gridContainerStyles(theme), padding: 0 }} className={"form_body"}>
            <Typography className="required_fields_label">required fields</Typography>
            <Typography className="optional_fields_label">optional fields</Typography>

            <Grid container direction="row" sx={{ marginTop: "5px" }} flexWrap="nowrap">
              <EditRounded className={`edit_icon ${legsAreDirty ? "active" : ""}`} sx={{ marginLeft: "15px" }} />
              <Typography variant="caption" sx={{ marginLeft: "10px" }}>
                Leg
              </Typography>
              <Typography variant="caption" sx={{ marginLeft: "14px" }}>
                Event Chip
              </Typography>
              <Typography variant="caption" sx={{ marginLeft: "69px" }}>
                Event Type
              </Typography>
              <Typography variant="caption" sx={{ marginLeft: "47px" }}>
                Location
              </Typography>
              <Typography variant="caption" sx={{ marginLeft: "60px" }}>
                Start
              </Typography>
              <Typography variant="caption" sx={{ marginLeft: "145px" }}>
                End
              </Typography>
              <Typography variant="caption" sx={{ marginLeft: "158px" }}>
                Duration
              </Typography>
              <Typography variant="caption" sx={{ marginLeft: "22px" }}>
                Aircraft
              </Typography>
            </Grid>

            <Grid container direction="column">
              {legs.map((event: INewGroundEvent, legIndex: number) => {
                return legIndex === editRow && event?._id?.includes("temp") ? (
                  <Grid item key={`flight_leg_${legIndex}`}>
                    <GroundEventRowEdit
                      initialValues={event}
                      legCount={legs?.length}
                      legIndex={legIndex}
                      mosaicPane={mosaicPane}
                      onSubmit={onSubmit}
                      theme={theme}
                      eventType={eventType}
                      displayAsUTC={displayAsUTC}
                      setEditRow={setEditRow}
                      focusField={focusField}
                      addNewLeg={handleAddNewLeg}
                    />
                  </Grid>
                ) : (
                  <Grid item key={`flight_leg_${legIndex}`}>
                    <GroundEventRowDisplay
                      event={event}
                      legIndex={legIndex}
                      mosaicPane={mosaicPane}
                      onSubmit={onSubmit}
                      theme={theme}
                      eventType={eventType}
                      displayAsUTC={displayAsUTC}
                      setEditRow={setEditRow}
                      setFocusField={setFocusField}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Grid>
        </Grid>

        <CardActions sx={formActionStyles(theme)}>
          <Button
            id={`${mosaicId}_resetFlights_btn`}
            className="form_button"
            variant="contained"
            color="secondary"
            disabled={!legsAreDirty}
            //type="button"
            onClick={() => resetAllChangesToLegs()}
          >
            Reset
          </Button>
          <Button
            id={`${mosaicId}_removeFlight_btn`}
            className="form_button"
            variant="contained"
            color="secondary"
            disabled={legs?.length === 0 || editRow === -1}
            //type="button"
            onClick={() => removeLeg(legs[editRow]?._id)}
          >
            <Remove style={{ marginRight: 5 }} />
            Remove Leg
          </Button>
          <Button
            id={`${mosaicId}_addFlight_btn`}
            className="form_button"
            variant="contained"
            color="primary"
            onClick={() => addLeg()}
          >
            <Addbox style={{ marginRight: 5 }} />
            Add New Leg
          </Button>
          <Grid item sx={{ marginTop: "-5px", width: 280 }}>
            <SwitchField
              className={`time_mode_selector ${!displayAsUTC && "active"}`}
              name={"editflight_time_mode_selector"}
              value={displayAsUTC}
              onChange={() => setDisplayAsUTC(!displayAsUTC)}
              switchlabels={["UTC", "LOCAL"]}
              switchtooltips={["Tooltips as UTC", "Tooltips as local"]}
              label="Display Block Info Tooltips in"
              //disabled={!values?.destination || !values?.origin}
              allowNull={false}
            />
          </Grid>
        </CardActions>
      </CardContent>
    </Card>
  );
};
